import { isObject, size, upperFirst } from 'lodash';
import {
  ElementPropertyResolvedCount,
  ElementPropertyType,
  IFactoryProperty,
} from '../models/element-property.model';
import {
  elementQuantityNames,
  ElementQuantityExpressionRecord,
  elementQuantityUnitRecord,
  FactoryQuantityRecord,
  IElementQuantityExpressionProperty,
  IFactoryQuantityExpressionProperty,
  ElementQuantityRecord,
  IElementQuantityProperty,
  IFactoryQuantityProperty,
  IElementQuantitySwitchProperty,
  IElementQuantitySelectProperty,
  elementQuantitySwitchNames,
  ElementQuantityName,
  ElementQuantitySwitchName,
  ElementQuantitySelectName,
  elementQuantitySelectNames,
  ElementQuantityExpressionName,
  ElementQuantityTypeByName,
  FactoryQuantityTypeByName,
  elementQuantityExpressionNames,
  sortedElementQuantityNames,
  DEFAULT_QUANTITY_EXPRESSION,
  DEFAULT_QUANTITY_EXPRESSIONS,
  elementQuantityRepeatingChildNames,
  RepeatingItemQuantityName,
  ElementPropertyOrQuantityProperty,
  ElementOrQuantityRecord,
} from '../models/element-quantities.model';
import {
  ExpressionValueFactoryType,
  Baseline,
  IElement,
  OneOfElements,
  OneOfParentElements,
  Project,
} from '../models/project.model';
import { ItemOrItemId } from '../models/type-helpers.model';
import { createRecordByKey, getItemByIdOrName, isOneOf } from './array.helpers';
import { createElementProperty } from './element-property-factory.helpers';
import {
  getCount,
  getElementPropertyResolvedCount,
  hasCount,
  PropertyChange,
  setElementProperties,
} from './element-property.helpers';
import {
  createExpression,
  isExpressionValue,
  isExpressionValueFactoryType,
  resolveFactoryCount,
} from './expression-factory.helpers';
import { isNumeric } from './math.helpers';
import { guards } from './typeguards.helpers';
import {
  applyChanges,
  filterObject,
  getId,
  hasDefinedProperties,
  isNonArrayObject,
  isPrimitive,
  mapFilterRecord,
  omitUndefined,
} from './object.helpers';
import { updateElements } from './project.helpers';
import { validateElementProperties } from '../validation/element-property.validation';
import {
  getElementById,
  isElement,
  isOneOfPropertyElements,
} from './recursive-element.helpers';
import { getTimestamp } from './date.helpers';
import { ElementCategoryID } from '../models/element-categories.model';
import { getElementCategory } from './element-category.helpers';
import { getElementCategoryId } from './element-category-id.helpers';
import { createId } from './id.helpers';

const EMPTY_QUANTITY_RECORD: Readonly<ElementQuantityRecord> = Object.freeze(
  {},
);
const REPEATING_DEFAULTS: Record<
  RepeatingItemQuantityName,
  ElementPropertyResolvedCount<
    ElementQuantityTypeByName<RepeatingItemQuantityName>['type']
  >
> = {
  repeating_item_spacing: 0,
  repeating_item_thickness: 0,
  repeating_item_count: 0,
  repeating_direction: 'horizontal',
};

/**
 * Check if name is a valid element quantity name
 * @param name
 * @returns
 */
export const isElementQuantityName = (
  name: unknown,
): name is ElementQuantityName => isOneOf(elementQuantityNames, name);

export const isElementQuantityRepeatingChildName = (
  name: string | undefined,
): name is (typeof elementQuantityRepeatingChildNames)[number] => {
  return isOneOf(elementQuantityRepeatingChildNames, name);
};

const isElementQuantitySwitchName = (
  name: unknown,
): name is ElementQuantitySwitchName =>
  isOneOf(elementQuantitySwitchNames, name);

const isElementQuantitySelectName = (
  name: unknown,
): name is ElementQuantitySelectName =>
  isOneOf(elementQuantitySelectNames, name);

export const isElementQuantityExpressionName = (
  name: unknown,
): name is ElementQuantityExpressionName =>
  isOneOf(elementQuantityExpressionNames, name);

export const isElementQuantityExpressionProperty = (
  property?: ElementPropertyOrQuantityProperty,
): property is IElementQuantityExpressionProperty =>
  !!property &&
  isElementQuantityExpressionName(property.name) &&
  !!getCount(property);

export const isElementQuantitySwitchProperty = (
  property?: ElementPropertyOrQuantityProperty,
): property is IElementQuantitySwitchProperty =>
  !!property &&
  isElementQuantitySwitchName(property.name) &&
  typeof getCount(property) === 'boolean';

export const isElementQuantitySelectProperty = (
  property?: ElementPropertyOrQuantityProperty,
): property is IElementQuantitySelectProperty =>
  isNonArrayObject(property) &&
  isElementQuantitySelectName(property.name) &&
  isElementQuantityPropertyOptions(
    (property as IElementQuantitySelectProperty).options,
  );

const isElementQuantityPropertyOptions = (
  options: unknown,
): options is IElementQuantitySelectProperty['options'] =>
  Array.isArray(options) || typeof options === 'string';

/**
 * Test if a property is a quantity property.
 * Any of the three types
 * @param property
 * @returns
 */
export const isElementQuantityProperty = (
  property?: ElementPropertyOrQuantityProperty,
): property is IElementQuantityProperty =>
  isElementQuantityExpressionProperty(property) ||
  isElementQuantitySwitchProperty(property) ||
  isElementQuantitySelectProperty(property);

const isFactoryQuantityExpressionProperty = (
  property: unknown,
): property is IFactoryQuantityExpressionProperty =>
  isObject(property) &&
  'name' in property &&
  isElementQuantityExpressionName(property.name);

export const isElementQuantityRecord = (
  record: unknown,
): record is ElementQuantityRecord => {
  if (!isObject(record)) {
    return false;
  }
  return Object.values(omitUndefined(record)).some(isElementQuantityProperty);
};

/**
 * Resolves the expression quantity row used for inline edits.
 * Falls back to the category template when the element tree is missing that key
 * (common on recipe-scoped children with a frozen {@link ProjectChangeType.ElementRecipeApply} snapshot).
 *
 * @param element - Element the user is editing (typically proposal-resolved)
 * @param quantityName - Quantity key to edit; defaults to {@link getSelectedQuantityName}
 */
const getCategoryQuantityTemplateProperty = (
  element: IElement,
  quantityName: ElementQuantityName,
): IElementQuantityExpressionProperty | undefined => {
  const templateRecord =
    getElementCategory(element)?.getQuantityProperties?.(element);
  if (!templateRecord) return undefined;

  const fromTemplate =
    createElementQuantityRecord(templateRecord)[quantityName];
  return isElementQuantityExpressionProperty(fromTemplate)
    ? fromTemplate
    : undefined;
};

export const getQuantityExpressionPropertyForEdit = (
  element: IElement,
  quantityName?: ElementQuantityName,
): IElementQuantityExpressionProperty | undefined => {
  const name = quantityName ?? getSelectedQuantityName(element);
  if (!name) return undefined;

  const existing = getElementQuantityRecord(element)[name];
  if (isElementQuantityExpressionProperty(existing)) return existing;

  return getCategoryQuantityTemplateProperty(element, name);
};

/**
 * Resolves the display unit for a quantity row, preferring the category template when
 * {@link IElementCategory.getQuantityProperties} is dynamic (e.g. energy SBEF).
 *
 * @param element - Element the user is editing (typically proposal-resolved)
 * @param quantityName - Quantity key; defaults to {@link getSelectedQuantityName}
 */
export const getQuantityDisplayUnitForEdit = (
  element: IElement,
  quantityName?: ElementQuantityName,
): string | undefined => {
  const name = quantityName ?? getSelectedQuantityName(element);
  if (!name) return undefined;

  const category = getElementCategory(element);
  if (category?.getQuantityProperties) {
    const templateProperty = getCategoryQuantityTemplateProperty(element, name);
    if (templateProperty) return templateProperty.displayUnit;
  }

  const existing = getElementQuantityRecord(element)[name];
  return isElementQuantityExpressionProperty(existing)
    ? existing.displayUnit
    : undefined;
};

/**
 * Get the selected element quantity property.
 * I.E. the quantity property that is listed in element list
 * @param element
 * @returns
 */
export const getSelectedElementQuantityProperty = (
  element: IElement,
): IElementQuantityExpressionProperty | undefined => {
  const selectedQuantity = getSelectedQuantityName(element);
  return selectedQuantity
    ? getQuantityExpressionPropertyForEdit(element, selectedQuantity)
    : undefined;
};

const isPlaceholderQuantityCountExpression = (
  expression: string | undefined,
): boolean => {
  const line = expression?.trim() ?? '';
  return line === '' || line === '0';
};

/**
 * Whether a quantity row has an explicit user `count`, not a placeholder line (`''` or `'0'`).
 * Numeric `count.value` is treated like a non-placeholder expression (e.g. EL value overrides).
 *
 * @param property - Quantity property on an element
 */
export const hasExplicitQuantityCount = (
  property: IElementQuantityProperty | undefined,
): boolean => {
  if (!hasCount(property)) return false;
  const count = property.count;
  if (!isExpressionValue(count)) return true;
  if (guards.is.number(count.value)) {
    return count.value !== 0;
  }
  return !isPlaceholderQuantityCountExpression(count.expression);
};

/**
 * Element snapshot for {@link propertyChangesToProjectChanges} under an active proposal.
 * Clears `count.value` when it only mirrors `count.resolved` on a non-literal expression
 * (e.g. inherited `gfa`) so typing a new override still emits {@link ProjectChangeType.ElementQuantityValue}
 * (DEV-3313).
 *
 * @param element - Proposal-resolved row the user edits
 * @param quantityName - Quantity column being saved
 */
export const elementForProposalQuantityProjectDiff = (
  element: IElement,
  quantityName: ElementQuantityName,
): IElement => {
  const record = getElementQuantityRecord(element);
  const row = record[quantityName];
  if (!isElementQuantityExpressionProperty(row)) return element;

  const count = row.count;
  if (!isExpressionValue(count)) return element;

  const { value, resolved, expression } = count;
  const expressionLine = expression?.trim() ?? '';
  if (
    value === undefined ||
    resolved === undefined ||
    value !== resolved ||
    expressionLine.length === 0 ||
    isNumeric(expressionLine)
  ) {
    return element;
  }

  return {
    ...element,
    quantity: {
      ...record,
      [quantityName]: {
        ...row,
        count: { ...count, value: undefined },
      },
    },
  };
};

/**
 * Get a property type by quantity name in a typesafe way.
 * @param name
 * @returns
 */
const getPropertyTypeByName = <T extends ElementQuantityName>(
  name: T,
): Required<ElementQuantityTypeByName<T>>['type'] => {
  if (isElementQuantitySwitchName(name)) {
    return ElementPropertyType.Switch;
  }
  if (isElementQuantitySelectName(name)) {
    return ElementPropertyType.Select;
  }
  if (isElementQuantityExpressionName(name)) {
    return ElementPropertyType.Expression;
  }
  throw new Error(`Invalid element quantity name: ${String(name)}`);
};

/**
 * Make a record of ElementQuantityProperties from an array of IElementQuantityProperty
 * @param properties
 * @returns
 */
export const toElementQuantityRecord = (
  ...properties: IElementQuantityProperty[]
): ElementQuantityRecord => createRecordByKey(properties, 'name');

const getQuantityExpressionDefaults = (
  defaults: IFactoryQuantityExpressionProperty,
) => ({
  min: 0, // No negative quantities for now
  fallbackCount: createExpression(
    getDefaultFallbackExpressionByName(defaults.name),
  ), // Allways provide a fallback count
  ...omitUndefined(defaults),
  unit: defaults.unit ?? elementQuantityUnitRecord[defaults.name],
});

/**
 * Create a new element quantity property from a set of defaults.
 * Should automatically be able to map to the correct type
 * @param defaults
 * @param regenerateId If true it will regerate id for all elements even if id already exist
 * @returns
 */
export const createElementQuantityProperty = <
  T extends IFactoryQuantityProperty,
  K extends T['name'],
>(
  defaults: T,
  regenerateId: boolean = false,
): ElementQuantityTypeByName<K> => {
  const name = defaults.name;
  const type = getPropertyTypeByName(name);

  if (defaults.type && defaults.type !== type) {
    throw new Error(
      `Invalid property type for ${name}, expected ${type} but got ${defaults.type}`,
    );
  }

  if (isFactoryQuantityExpressionProperty(defaults)) {
    defaults = getQuantityExpressionDefaults(defaults) as T;
  } else if (isExpressionValue(defaults.fallbackCount)) {
    // Self-heal rows persisted before DEV-3704: auto-quantity used to stamp an
    // expression fallbackCount on select/switch quantities, which fails
    // validation when the row is rebuilt (proposal resolution, copies).
    const { fallbackCount: _invalid, ...rest } = defaults;
    defaults = rest as T;
  }

  const property = createElementProperty({
    ...omitUndefined(defaults),
    type,
    updated_at: getTimestamp(),
    id: createId(defaults, regenerateId),
  } as IFactoryProperty);
  if (isElementQuantityProperty(property)) {
    return property as ElementQuantityTypeByName<K>;
  }
  throw new Error('Could not create element quantity property');
};

/**
 * Create an entire ElementQuantityRecord from a FactoryQuantityRecord or array of IFactoryQuantityProperty
 * @param defaults
 * @returns
 */
export const createElementQuantityRecord = (
  defaults: FactoryQuantityRecord | IFactoryQuantityProperty[] = [],
  regenerateId?: boolean,
): ElementQuantityRecord => {
  const factoryRecord = toFactoryQuantityRecord(defaults);

  return toElementQuantityRecord(
    ...Object.values(factoryRecord).map((value) =>
      createElementQuantityProperty(
        value as IFactoryQuantityProperty,
        regenerateId,
      ),
    ),
  );
};

/**
 * Create an entire ElementQuantityRecord from a FactoryQuantityRecord
 * @param defaults
 * @returns
 */
const toFactoryQuantityRecord = (
  defaults: FactoryQuantityRecord | IFactoryQuantityProperty[] = {},
): FactoryQuantityRecord => {
  const arrayWithNames = Object.entries(defaults).map(([key, value]) => {
    const name = isElementQuantityName(key)
      ? key
      : isObject(value) && 'name' in value && value.name;
    if (!isElementQuantityName(name)) {
      throw new Error(`Invalid element quantity name: ${String(name)}`);
    }
    return toFactoryQuantityProperty(value, name);
  });

  return createRecordByKey(arrayWithNames, 'name');
};

const toFactoryQuantityProperty = <
  T extends FactoryQuantityRecord[K],
  K extends ElementQuantityName,
>(
  value: T,
  name: K,
): FactoryQuantityTypeByName<K> => {
  // Create a QuantityProperty with name and count
  const property: FactoryQuantityTypeByName<K> = {
    ...(isPrimitive(value) || isExpressionValueFactoryType(value)
      ? { count: value as FactoryQuantityTypeByName<K>['count'] } // Value is a Factory count
      : (value as Partial<FactoryQuantityTypeByName<K>>)), // Value is already a QuantityProperty
    name,
  } as FactoryQuantityTypeByName<K>;

  // For expressions we need to create a count property as an ExpressionValue
  return isElementQuantityExpressionName(name)
    ? resolveFactoryCount({
        fallbackCount: createExpression(
          getDefaultFallbackExpressionByName(name),
        ),
        ...property,
      } as {
        count?: ExpressionValueFactoryType | undefined;
        fallbackCount?: ExpressionValueFactoryType | undefined;
      })
    : property;
};

/**
 * Get all quantity properties for an element as an array.
 * @param elementOrQuantity
 * @returns
 */
export const getElementQuantityProperties = (
  elementOrQuantity?: ElementOrQuantityRecord,
): IElementQuantityProperty[] =>
  Object.values(
    filterObject(
      getElementQuantityRecord(elementOrQuantity),
      isElementQuantityProperty,
    ),
  ).sort(sortQuantityPropertiesFn);

const quantityNamesPrio: ElementQuantityName[] = [
  'mass',
  'volume',
  'area_side',
  'area_top',
  'width',
  'height',
  'length',
  'time',
  'energy',
  'density',
] as const;

/**
 * Get the default selected quantity name for an element.
 * @param element
 * @param categoryId
 * @returns
 */
export const getDefaultSelectedQuantityName = (
  element: IElement,
  categoryId: ElementCategoryID | undefined = getElementCategoryId(element),
): ElementQuantityName | undefined => {
  const quantity = getElementQuantityRecord(element);
  if (size(quantity) === 0) {
    return;
  }
  const quantityName =
    element.selectedQuantity ?? getSelectedQuantityNameByCategoryId(categoryId);

  // If quantity exist and is an expression property, return it
  if (
    quantityName &&
    isElementQuantityExpressionProperty(quantity[quantityName])
  ) {
    return quantityName;
  }

  // Return the first quantity with a count or else fallback count
  return (
    quantityNamesPrio.find((name) => quantity[name]?.count) ??
    quantityNamesPrio.find((name) => quantity[name]?.fallbackCount)
  );
};

/**
 * Get the selected quantity name for an element.
 * Will automatically fallback if undefined and quantities are available.
 * @param element
 * @returns
 */
export const getSelectedQuantityName = (
  element: IElement,
): ElementQuantityName | undefined => {
  const { selectedQuantity } = element;
  return selectedQuantity ?? getDefaultSelectedQuantityName(element);
};

/**
 * Select or clear element quantity
 * @param element
 * @param name New selected quantity name. If undefined, clear selected quantity
 * @returns
 */
export const selectElementQuantity = (
  element: IElement,
  name: 'auto' | ElementQuantityName | undefined,
): IElement => {
  const { quantity, selectedQuantity } = element;

  // If no quantity, clear selected quantity
  if (!quantity || !size(quantity) || !name) {
    return selectedQuantity
      ? { ...element, selectedQuantity: undefined }
      : element;
  }

  // If selected quantity does not exist, use default quantity name
  if (name === 'auto' || !isElementQuantityExpressionProperty(quantity[name])) {
    name = getDefaultSelectedQuantityName(element);
  }

  // If selected quantity is different, update it
  return name !== selectedQuantity
    ? { ...element, selectedQuantity: name }
    : element;
};

/**
 * Get the default selected quantity name for a category.
 * TODO: Maybe nicer to add to element category
 * @param categoryId
 * @returns
 */
export const getSelectedQuantityNameByCategoryId = (
  categoryId: Parameters<typeof getElementCategoryId>[0],
): ElementQuantityName | undefined => {
  switch (getElementCategoryId(categoryId)) {
    case ElementCategoryID.Wall:
    case ElementCategoryID.WindowsDoors:
      return 'area_side';
    case ElementCategoryID.Slab:
    case ElementCategoryID.Other:
      return 'area_top';
    case ElementCategoryID.Column:
      return 'height';
    case ElementCategoryID.Beam:
      return 'length';
    case ElementCategoryID.Concrete:
    case ElementCategoryID.Insulation:
    case ElementCategoryID.Wood:
    case ElementCategoryID.Ceramics:
    case ElementCategoryID.Gypsum:
    case ElementCategoryID.Metal:
    case ElementCategoryID.OtherProduct:
      return 'mass';
    case ElementCategoryID.Labour:
      return 'time';
    case ElementCategoryID.Energy:
    case ElementCategoryID.AirHandlingUnit:
    case ElementCategoryID.Heating:
    case ElementCategoryID.Cooling:
      return 'energy';
  }
};

/**
 * Default product {@link IProductElement.count} expression when adding a product under a
 * category element — same rule as {@link toCategoryChildFactoryElement} in element-category.
 */
export const getDefaultProductCountExpressionFromParent = (
  parent: IElement,
): string => {
  const parentQuantity = getElementQuantityRecord(parent);
  if (hasCount(parentQuantity.mass)) return 'mass';

  const categoryId = getElementCategoryId(parent);
  const categoryDefaultQuantity = categoryId
    ? getSelectedQuantityNameByCategoryId(categoryId)
    : undefined;

  // Mass-category elements keep product count on mass even before mass has a count
  // (e.g. OtherProduct, Insulation). Walls with only template mass use selectedQuantity (DEV-3365).
  if (parentQuantity.mass && categoryDefaultQuantity === 'mass') {
    return 'mass';
  }

  return parent.selectedQuantity ?? categoryDefaultQuantity ?? 'mass';
};

/**
 * Get element quantity record from element or quantity record
 * @param elementOrQuantity
 * @returns
 */
export const getElementQuantityRecord = (
  elementOrQuantity: ElementOrQuantityRecord = {},
): ElementQuantityRecord => {
  if (isElementQuantityRecord(elementOrQuantity)) {
    return elementOrQuantity;
  }
  const quantity =
    'quantity' in elementOrQuantity
      ? elementOrQuantity.quantity
      : EMPTY_QUANTITY_RECORD;

  return isElementQuantityRecord(quantity) ? quantity : EMPTY_QUANTITY_RECORD;
};

/**
 * Quantity partial that clears an expression `fallbackCount` from select/switch
 * quantity rows, or `undefined` when every row is already valid. Rows persisted
 * before DEV-3704 carry that shape (auto-quantity stamped it on a circular
 * record); a select/switch row only ever has a primitive fallback.
 * @param elementOrQuantity
 * @returns
 */
export const getInvalidSelectSwitchFallbackClears = (
  elementOrQuantity: ElementOrQuantityRecord,
): Partial<ElementQuantityRecord> | undefined => {
  const quantity = getElementQuantityRecord(elementOrQuantity);
  let clears: Partial<ElementQuantityRecord> | undefined;
  for (const property of Object.values(quantity)) {
    if (
      !property ||
      isElementQuantityExpressionName(property.name) ||
      !isExpressionValue(property.fallbackCount)
    ) {
      continue;
    }
    clears ??= {};
    (clears as Record<string, unknown>)[property.name] = {
      fallbackCount: undefined,
    };
  }
  return clears;
};

/**
 * Get the expression-typed quantity rows of an element or quantity record.
 *
 * Select/switch rows (`repeating_direction`, `repeating_items`) are excluded by
 * name: the record feeds auto-quantity, which writes expression fallbackCounts
 * onto every row it holds — an expression fallback on a select row fails
 * validation and breaks proposal resolution (DEV-3704). Name-based so a legacy
 * row without count/fallbackCount is still an expression row.
 * @param elementOrQuantity
 * @returns
 */
export const getElementQuantityExpressionRecord = (
  elementOrQuantity: ElementOrQuantityRecord = {},
): ElementQuantityExpressionRecord =>
  filterObject(
    getElementQuantityRecord(elementOrQuantity),
    (property): property is IElementQuantityExpressionProperty =>
      isElementQuantityProperty(property) &&
      isElementQuantityExpressionName(property.name),
  );

/**
 * True when the element quantity template includes mass or areal density (kg/m²).
 * Gates proposal enrich auto-quantity and narrow expression-variable collection (DEV-3349).
 */
export const elementHasMassRelatedQuantityExpressions = (
  element: IElement,
): boolean => {
  const quantity = getElementQuantityExpressionRecord(element);
  return !!(
    quantity.mass ||
    quantity.density_areal_top ||
    quantity.density_areal_side
  );
};

export const getElementQuantityByName = <T extends ElementQuantityName>(
  elementOrQuantity: ElementOrQuantityRecord,
  name: T,
): ElementQuantityTypeByName<T> | undefined =>
  getElementQuantityRecord(elementOrQuantity)[name] as
    ElementQuantityTypeByName<T> | undefined;

export const getElementQuantityCountByName = <T extends ElementQuantityName>(
  elementOrQuantity: ElementOrQuantityRecord,
  name: T,
): ElementQuantityTypeByName<T>['count'] | undefined => {
  const quantity = getElementQuantityByName(elementOrQuantity, name);
  return quantity ? getCount(quantity) : undefined;
};

export const getElementQuantityResolvedCountByName = <
  T extends ElementQuantityName,
  D extends
    | ElementPropertyResolvedCount<ElementQuantityTypeByName<T>['type']>
    | undefined,
  V = ElementPropertyResolvedCount<ElementQuantityTypeByName<T>['type']>,
  R = D extends undefined ? V | undefined : V,
>(
  elementOrQuantity: ElementOrQuantityRecord,
  name: T,
  defaultValue?: D,
): R => {
  const quantity = getElementQuantityByName(elementOrQuantity, name);
  return (
    quantity
      ? getElementPropertyResolvedCount(quantity, defaultValue)
      : defaultValue
  ) as R;
};

export const getRepeatingResolvedCountByName = <
  T extends RepeatingItemQuantityName,
  R extends ElementPropertyResolvedCount<ElementQuantityTypeByName<T>['type']>,
>(
  elementOrQuantity: ElementOrQuantityRecord,
  name: T,
): R => {
  const isRepeating = getElementQuantityResolvedCountByName(
    elementOrQuantity,
    'repeating_items',
  );
  if (isRepeating) {
    return (
      getElementQuantityResolvedCountByName(elementOrQuantity, name) ??
      REPEATING_DEFAULTS[name]
    );
  }

  return REPEATING_DEFAULTS[name] as R;
};

/**
 * Between 0 to 1 how much space an element occupies taking account of repeating items and fill.
 * Illustation below with spacing = 4, width = 1, fill = 1 gives a fill grade of 0.2
 * []----[]----[]----[]----[]----[]----[]
 * @param element
 */
export const getElementFillGrade = (element: IElement): number => {
  const spacing = getRepeatingResolvedCountByName(
    element,
    'repeating_item_spacing',
  );
  const width = getRepeatingResolvedCountByName(
    element,
    'repeating_item_thickness',
  );
  const fill = getElementQuantityResolvedCountByName(element, 'fill') ?? 1;
  return (!spacing || !width ? 1 : width / (width + spacing)) * fill;
};

export const getElementQuantityFormattedName = (key: string): string => {
  const names: Partial<Record<ElementQuantityName, string>> = {
    area_side: 'Side area',
    area_top: 'Top area',
    area_section: 'Section area',
    density_areal_side: 'Areal density',
    repeating_item_spacing: 'Item spacing',
    repeating_item_thickness: 'Item thickness',
    repeating_item_count: 'Item count',
    repeating_items: 'Repeating items',
    repeating_direction: 'Repeating direction',
    time: 'Time of work',
  };
  return (isElementQuantityName(key) && names[key]) || upperFirst(key);
};

/**
 * Update element quantity with one or more changes.
 * Will add any missing property
 * @param element
 * @param elementChanges
 * @param root Used to find element parent
 * @returns
 */
export const setElementQuantityProperties = (
  element: IElement,
  changes: FactoryQuantityRecord | IFactoryQuantityProperty[],
): IElement => {
  const quantity = getElementQuantityRecord(element);
  const updatedElement = applyChanges(element, {
    quantity: setQuantityRecord(quantity, changes),
  });
  if (updatedElement !== element) {
    // Validate on change only
    validateElementProperties(getElementQuantityProperties(updatedElement));
  }

  return updatedElement;
};

const setQuantityRecord = (
  quantityRecord: ElementQuantityRecord,
  changes: FactoryQuantityRecord | IFactoryQuantityProperty[],
): ElementQuantityRecord => {
  const changeRecord = toFactoryQuantityRecord(changes);
  const changesArray = (
    Object.values(changeRecord) as IFactoryQuantityProperty[]
  ).map((value) => {
    if (!value.name) {
      throw new Error(`Invalid element quantity property, no name provided`);
    }
    const current = quantityRecord[value.name];
    return !current ? createElementQuantityProperty(value) : value;
  });

  const updatedRecord = applyChanges(
    quantityRecord,
    createRecordByKey(changesArray, 'name'),
  );

  // Apply updated_at to all changed properties
  return applyUpdatedAtDatesToQuantityRecord(updatedRecord, quantityRecord);
};

/**
 * Detect what have changed and apply new updated_at dates to those properties
 * @param updatedRecord
 * @param originalRecord
 * @returns
 */
const applyUpdatedAtDatesToQuantityRecord = (
  updatedRecord: ElementQuantityRecord,
  originalRecord: ElementQuantityRecord,
): ElementQuantityRecord => {
  // Don't modify if nothing have changed
  if (updatedRecord === originalRecord) {
    return originalRecord;
  }
  return mapFilterRecord(updatedRecord, (value) => {
    if (
      value &&
      (value.count as { expression: unknown })?.expression !==
        (originalRecord[value.name]?.count as { expression: unknown })
          ?.expression
    ) {
      return { ...value, updated_at: getTimestamp() };
    }
    return value;
  }) as ElementQuantityRecord;
};

/**
 * Update an element's quantity with one or more changes
 * and apply the element changes to the root.
 * Will add any missing property
 * @param root
 * @param elementOrId
 * @param quantityChanges
 * @returns
 */
export const updateElementQuantityPropertiesInRoot = <
  R extends Project | Baseline,
>(
  root: R,
  elementOrId: ItemOrItemId<OneOfElements>,
  quantityChanges: FactoryQuantityRecord | IFactoryQuantityProperty[],
): R => {
  const elementId = getId(elementOrId);

  // Make sure to fetch the latest instance to not overwrite any changes previously made
  const element = getElementById(root, elementId);

  if (!isElement(element)) {
    throw new Error(`Element not found with id ${elementId}`);
  }
  const updatedElement = setElementQuantityProperties(element, quantityChanges);

  // Update element if properties have changed else return project unmodified
  if (updatedElement !== element) {
    return updateElements(root, updatedElement);
  }
  return root;
};

/**
 * Update an element's quantity and properties and apply the changes to the root (project or building version).
 * Good if you want to update both properties and quantity at the same time
 * @param root
 * @param element
 * @param changes
 * @returns
 */
export const updateElementQuantityAndPropertiesInRoot = <
  R extends Project | Baseline,
>(
  root: R,
  element: OneOfParentElements,
  ...changes: PropertyChange[]
): R => {
  if (!isOneOfPropertyElements(element) || changes.length === 0) {
    return root;
  }
  const quantities = Object.values(getElementQuantityProperties(element));

  // Changes to quantity properties
  const quantityChanges = changes.filter(
    (change) => !!getItemByIdOrName(quantities, change, false),
  );

  // Changes to properties are the rest
  const propertyChanges = changes.filter(
    (change) => !quantityChanges.includes(change),
  );

  // Only IElement have quantity properties currently
  if (isElement(element)) {
    root = updateElementQuantityPropertiesInRoot(
      root,
      element,
      quantityChanges as IFactoryQuantityProperty[],
    );
  }
  return setElementProperties(root, element, ...propertyChanges);
};

export const hasQuantityProperties = (element: OneOfElements): boolean =>
  getElementQuantityProperties(element).length > 0;

const sortQuantityPropertiesFn = (
  p1: IElementQuantityProperty,
  p2: IElementQuantityProperty,
) =>
  sortedElementQuantityNames.indexOf(p1.name) -
  sortedElementQuantityNames.indexOf(p2.name);

export const getDefaultFallbackExpressionByName = (name?: string): string =>
  DEFAULT_QUANTITY_EXPRESSIONS[name as ElementQuantityExpressionName] ??
  DEFAULT_QUANTITY_EXPRESSION;

export const getDefinedQuantities = (
  quantity: ElementQuantityRecord | undefined,
): Record<string, IElementQuantityExpressionProperty> => {
  const record = getElementQuantityExpressionRecord(quantity);

  if (!hasDefinedProperties(record)) {
    throw new Error('Quantities are missing');
  }
  return record;
};
